import React, { useEffect } from 'react';
import { Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import { getPreset } from '../textStyle.js';

const ComparisonTable = ({ 
  title = "Product Comparison",
  productA = "Product A", 
  productB = "Product B",
  productAImage = null,
  productBImage = null,
  rows = [],
  titleStylePreset = 'compareTableTableTitle',
  productNameStylePreset = 'compareTableProdctName',
  featureTitleStylePreset = 'compareTableFeatureTitle',
  featureDescStylePreset = 'compareTableFeatureDescription'
}) => {
  console.log('🔍 ComparisonTable render:', {
    title,
    productA,
    productB,
    productAImage,
    productBImage,
    hasProductAImage: !!productAImage,
    hasProductBImage: !!productBImage,
    rowsCount: rows.length
  });

  // Force re-render when image props change
  useEffect(() => {
    console.log('🔄 ComparisonTable useEffect - Images changed:', {
      productAImage,
      productBImage
    });
  }, [productAImage, productBImage]);
  return (
    <Box sx={{ my: 4 }}>
      {title && (
        <Typography 
          variant="h5" 
          sx={{ 
            mb: 3,
            ...getPreset(titleStylePreset),
            fontFamily: getPreset(titleStylePreset)?.fontFamily || 'inherit',
            fontSize: getPreset(titleStylePreset)?.fontSize || 'inherit',
            fontWeight: getPreset(titleStylePreset)?.fontWeight || 'bold',
            color: getPreset(titleStylePreset)?.color || 'inherit',
            textAlign: getPreset(titleStylePreset)?.textAlign || 'center',
            lineHeight: getPreset(titleStylePreset)?.lineHeight || 'inherit',
            letterSpacing: getPreset(titleStylePreset)?.letterSpacing || 'inherit'
          }}
        >
          {title}
        </Typography>
      )}
      
      <TableContainer component={Paper} sx={{ boxShadow: 2 }}>
        <Table>
          <TableHead>
            {/* Product Images Row (if images are provided) */}
            {(productAImage || productBImage) && (
              <TableRow>
                <TableCell sx={{ width: '40%', border: 'none', p: 1 }}>
                  {/* Empty cell for feature column */}
                </TableCell>
                <TableCell 
                  align="center" 
                  sx={{ 
                    width: '30%', 
                    border: 'none', 
                    p: 1,
                    backgroundColor: '#B8E6E1'
                  }}
                >
                  {productAImage && (
                    <img 
                      src={productAImage} 
                      alt={productA}
                      style={{
                        width: '100px',
                        height: '100px',
                        objectFit: 'cover',
                        borderRadius: '8px',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                      }}
                      onError={(e) => {
                        console.error('Failed to load Product A image:', productAImage);
                        e.target.style.display = 'none';
                      }}
                      onLoad={() => {
                        console.log('Product A image loaded successfully:', productAImage);
                      }}
                    />
                  )}
                </TableCell>
                <TableCell 
                  align="center" 
                  sx={{ 
                    width: '30%', 
                    border: 'none', 
                    p: 1,
                    backgroundColor: '#ffebee'
                  }}
                >
                  {productBImage && (
                    <img 
                      src={productBImage} 
                      alt={productB}
                      style={{
                        width: '100px',
                        height: '100px',
                        objectFit: 'cover',
                        borderRadius: '8px',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                      }}
                      onError={(e) => {
                        console.error('Failed to load Product B image:', productBImage);
                        e.target.style.display = 'none';
                      }}
                      onLoad={() => {
                        console.log('Product B image loaded successfully:', productBImage);
                      }}
                    />
                  )}
                </TableCell>
              </TableRow>
            )}
            
            {/* Product Names Header Row */}
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold', fontSize: '16px', width: '40%' }}>
                Feature
              </TableCell>
              <TableCell 
                align="center" 
                sx={{ 
                  backgroundColor: '#B8E6E1',
                  width: '30%',
                  ...getPreset(productNameStylePreset),
                  fontFamily: getPreset(productNameStylePreset)?.fontFamily || 'inherit',
                  fontSize: getPreset(productNameStylePreset)?.fontSize || '16px',
                  fontWeight: getPreset(productNameStylePreset)?.fontWeight || 'bold',
                  color: getPreset(productNameStylePreset)?.color || '#2D5A5A',
                  textAlign: getPreset(productNameStylePreset)?.textAlign || 'center',
                  lineHeight: getPreset(productNameStylePreset)?.lineHeight || 'inherit',
                  letterSpacing: getPreset(productNameStylePreset)?.letterSpacing || 'inherit'
                }}
              >
                {productA}
              </TableCell>
              <TableCell 
                align="center" 
                sx={{ 
                  backgroundColor: '#ffebee',
                  width: '30%',
                  ...getPreset(productNameStylePreset),
                  fontFamily: getPreset(productNameStylePreset)?.fontFamily || 'inherit',
                  fontSize: getPreset(productNameStylePreset)?.fontSize || '16px',
                  fontWeight: getPreset(productNameStylePreset)?.fontWeight || 'bold',
                  color: getPreset(productNameStylePreset)?.color || '#c62828',
                  textAlign: getPreset(productNameStylePreset)?.textAlign || 'center',
                  lineHeight: getPreset(productNameStylePreset)?.lineHeight || 'inherit',
                  letterSpacing: getPreset(productNameStylePreset)?.letterSpacing || 'inherit'
                }}
              >
                {productB}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row, index) => (
              <TableRow key={index} sx={{ '&:nth-of-type(odd)': { backgroundColor: '#f9f9f9' } }}>
                <TableCell sx={{ verticalAlign: 'top', py: 2 }}>
                  <Typography 
                    variant="body1" 
                    sx={{ 
                      mb: 0.5,
                      ...getPreset(featureTitleStylePreset),
                      fontFamily: getPreset(featureTitleStylePreset)?.fontFamily || 'inherit',
                      fontSize: getPreset(featureTitleStylePreset)?.fontSize || 'inherit',
                      fontWeight: getPreset(featureTitleStylePreset)?.fontWeight || 'medium',
                      color: getPreset(featureTitleStylePreset)?.color || 'inherit',
                      textAlign: getPreset(featureTitleStylePreset)?.textAlign || 'left',
                      lineHeight: getPreset(featureTitleStylePreset)?.lineHeight || 'inherit',
                      letterSpacing: getPreset(featureTitleStylePreset)?.letterSpacing || 'inherit'
                    }}
                  >
                    {row.feature}
                  </Typography>
                  {row.description && (
                    <Typography 
                      variant="body2" 
                      sx={{
                        ...getPreset(featureDescStylePreset),
                        fontFamily: getPreset(featureDescStylePreset)?.fontFamily || 'inherit',
                        fontSize: getPreset(featureDescStylePreset)?.fontSize || 'inherit',
                        fontWeight: getPreset(featureDescStylePreset)?.fontWeight || 'inherit',
                        color: getPreset(featureDescStylePreset)?.color || 'text.secondary',
                        textAlign: getPreset(featureDescStylePreset)?.textAlign || 'left',
                        lineHeight: getPreset(featureDescStylePreset)?.lineHeight || 'inherit',
                        letterSpacing: getPreset(featureDescStylePreset)?.letterSpacing || 'inherit'
                      }}
                    >
                      {row.description}
                    </Typography>
                  )}
                </TableCell>
                <TableCell align="center" sx={{ backgroundColor: row.productA ? '#B8E6E1' : '#fff' }}>
                  {row.productA ? (
                    <CheckCircleIcon sx={{ color: '#2D5A5A', fontSize: 30 }} />
                  ) : (
                    <CancelIcon sx={{ color: '#c62828', fontSize: 30 }} />
                  )}
                </TableCell>
                <TableCell align="center" sx={{ backgroundColor: row.productB ? '#B8E6E1' : '#ffebee' }}>
                  {row.productB ? (
                    <CheckCircleIcon sx={{ color: '#2D5A5A', fontSize: 30 }} />
                  ) : (
                    <CancelIcon sx={{ color: '#c62828', fontSize: 30 }} />
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default ComparisonTable; 